import { useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import ProductShell from "../components/ProductShell";
import { useAuth } from "../context/AuthContext";
import { functions } from "../firebase";
import { formatPublicHandle } from "../utils/identity";

function formatTrialDate(value) {
  if (!value) {
    return null;
  }

  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  }).format(date);
}

export default function Billing() {
  const { user, userProfile, workspace } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const trialEndsLabel = useMemo(
    () => formatTrialDate(userProfile?.trialEndsAt),
    [userProfile?.trialEndsAt]
  );

  const openBillingPortal = async () => {
    setLoading(true);
    setError("");

    try {
      const createBillingPortalSession = httpsCallable(functions, "createBillingPortalSession");
      const result = await createBillingPortalSession({
        origin: window.location.origin
      });

      window.location.assign(result.data.url);
    } catch (portalError) {
      setError(portalError.message || "Unable to open billing management.");
      setLoading(false);
    }
  };

  return (
    <ProductShell
      action={(
        <button disabled={loading || !userProfile?.stripeCustomerId} onClick={openBillingPortal} type="button">
          {loading ? "Opening..." : "Manage Billing"}
        </button>
      )}
      subtitle="Open the secure Stripe billing portal to cancel your trial or subscription before the next charge."
      title="Billing"
    >
      <div className="dashboard-shell product-page">
        {error ? <p className="status error-text mobile-banner">{error}</p> : null}

        <section className="dashboard-grid metrics-grid">
          <article className="card finance-card mobile-section-card">
            <span className="eyebrow">Current Plan</span>
            <strong>{workspace?.planName || userProfile?.planName || "Basic"}</strong>
            <p className="muted-text">Signed in as {userProfile?.publicHandle || formatPublicHandle({ email: user?.email })}.</p>
          </article>

          <article className="card finance-card mobile-section-card">
            <span className="eyebrow">Subscription Status</span>
            <strong>{userProfile?.subscriptionStatus || "No active subscription"}</strong>
            <p className="muted-text">Stripe updates this after checkout, trial start, cancellation, or renewal.</p>
          </article>

          <article className="card finance-card mobile-section-card">
            <span className="eyebrow">Trial Ends</span>
            <strong>{trialEndsLabel || "No active trial"}</strong>
            <p className="muted-text">Cancel in the billing portal before this date to avoid the first Pro charge.</p>
          </article>
        </section>

        <section className="card admin-card mobile-section-card">
          <h2>Cancel your Pro trial or subscription</h2>
          <p className="muted-text">
            Use the billing portal to cancel your Pro trial, update payment details, or manage future renewals. If your
            Pro trial is active, cancel before the trial end date so Stripe does not create the first paid invoice.
          </p>
          <div className="billing-action-row">
            <button className="mobile-action-button" disabled={loading || !userProfile?.stripeCustomerId} onClick={openBillingPortal} type="button">
              {loading ? "Opening..." : "Open Stripe Billing Portal"}
            </button>
            {!userProfile?.stripeCustomerId ? (
              <p className="muted-text">Start a paid plan or trial first, then billing management will be available here.</p>
            ) : null}
          </div>
        </section>
      </div>
    </ProductShell>
  );
}
